import { Box, Flex, Image, Text } from "@chakra-ui/react";
import { motion, useReducedMotion } from "framer-motion";
import { PiHandWavingLight } from "react-icons/pi";

const MBox = motion(Box);

/** Runs when the image scrolls into view (`whileInView`). */
const fromLeft = {
  hidden: { opacity: 0, x: -80 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.62, ease: [0.45, 0, 0.55, 1] },
  },
};

const fromRight = {
  hidden: { opacity: 0, x: 80 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.62, ease: [0.45, 0, 0.55, 1] },
  },
};

const viewport = { once: false, amount: 0.22, margin: "0px 0px -10% 0px" };

function AboutScrollImage({ side, reduceMotion, children, ...props }) {
  if (reduceMotion) {
    return (
      <Box overflow="hidden" {...props}>
        {children}
      </Box>
    );
  }
  return (
    <MBox
      overflow="hidden"
      variants={side === "left" ? fromLeft : fromRight}
      initial="hidden"
      whileInView="visible"
      viewport={viewport}
      {...props}
    >
      {children}
    </MBox>
  );
}

const About = () => {
  const reduceMotion = useReducedMotion();

  return (
    <Flex className="about" paddingX="10%" flexDirection="column">
      <Flex my={5} height="100px" alignItems="center" gap={5}>
        <AboutScrollImage
          side="left"
          reduceMotion={reduceMotion}
          flexShrink={0}
          rounded="full"
          boxSize="100px"
        >
          <Image
            src="./personalphoto.jpg"
            rounded="full"
            w="100%"
            h="100%"
            objectFit="cover"
            alt="Negin Alipanahi"
          />
        </AboutScrollImage>
        <Box height="inherit" fontSize={["14px", "16px"]}>
          <Flex>
            <Text>Hey!</Text>
            <PiHandWavingLight size={20} />
          </Flex>
          <Text> This is Negin. /nəˈɡin/</Text>
          <Text align="left"> Fullstack - based in Toronto</Text>
        </Box>
      </Flex>

      <Box my={5}>
        <AboutScrollImage
          side="right"
          reduceMotion={reduceMotion}
          display={{ base: "none", sm: "block" }}
          width={{ base: "100%", sm: "50%" }}
          float="right"
          pl={{ base: "0", sm: "5" }}
          pb={{ base: "5", sm: "0" }}
          borderRadius="md"
        >
          <Image
            src="./coding.jpg"
            width="100%"
            height="auto"
            display="block"
            alt="Coding workspace"
          />
        </AboutScrollImage>
        <Text
          fontSize={{ base: "20px", md: "28px", lg: "36px" }}
          fontFamily="Title"
          align="left"
          color="lightBlue"
          mb={2}
        >
          FROM SYSTEMS TO SOFTWARE
        </Text>
        <AboutScrollImage
          side="right"
          reduceMotion={reduceMotion}
          display={{ base: "block", sm: "none" }}
          width={{ base: "100%", sm: "50%" }}
          float="right"
          pl={{ base: "0", sm: "5" }}
          pb={{ base: "5", sm: "0" }}
          borderRadius="md"
        >
          <Image
            src="./coding.jpg"
            width="100%"
            height="auto"
            display="block"
            alt="Coding workspace"
          />
        </AboutScrollImage>
        <Text>
          I started my career in Industrial Engineering, where I developed a strong
          foundation in systems thinking, process design, and problem-solving. In my
          early roles (2014–2018), I worked closely with operations and project teams,
          gaining firsthand experience in how complex, real-world processes are
          structured—and where they break down. <br />
          <br />
          In 2019, I transitioned into web development, bringing that operational
          perspective into building software. Rather than focusing only on the technical
          side, I approach development as a way to translate complex workflows into
          clear, reliable, and user-focused systems. <br />
          <br />
          Since then, I’ve worked primarily with JavaScript, React, TypeScript, and
          Node.js, building applications that go beyond standard interfaces—often
          supporting real-world operations where usability, accuracy, and reliability
          directly impact outcomes. <br />
          <br />
          My background allows me to bridge the gap between business needs and technical
          implementation. I’m particularly interested in building products that solve
          practical problems, where understanding how things work in reality is just as
          important as writing clean code.
        </Text>
      </Box>

      <Box my={5}>
        <Flex
          flexDirection={["column-reverse", "column-reverse", "row"]}
          gap={5}
        >
          <AboutScrollImage
            side="left"
            reduceMotion={reduceMotion}
            width={["100%", "100%", "50%"]}
            borderRadius="md"
          >
            <Image
              src="./moon.jpg"
              width="100%"
              height="auto"
              display="block"
              alt="Moon photography"
            />
          </AboutScrollImage>
          <Box>
            <Text
              fontSize={{ base: "20px", md: "28px", lg: "36px" }}
              fontFamily="Title"
              color="lightBlue"
              align="left"
              mb={2}
            >
              THROUGH MY EYES
            </Text>
            Photography has been a lifelong escape for me. Through the lens,
            I explore different perspectives, play with light and shadow,
            and document moments that inspire and evoke emotion. <br /> This
            passion for photography enhances my attention to detail and my
            appreciation for aesthetics, both of which are crucial in my
            approach to design and development.
          </Box>
        </Flex>
      </Box>

      <Box my={5}>
        <AboutScrollImage
          side="right"
          reduceMotion={reduceMotion}
          display={{ base: "none", sm: "block" }}
          width={{ base: "100%", sm: "50%" }}
          float="right"
          pl={{ base: "0", sm: "5" }}
          pb={{ base: "5", sm: "0" }}
          borderRadius="md"
        >
          <Image
            src="./nealphi_grayscale.jpg"
            width="100%"
            height="auto"
            display="block"
            alt="Studio and sewing"
          />
        </AboutScrollImage>
        <Text
          fontSize={{ base: "20px", md: "28px", lg: "36px" }}
          fontFamily="Title"
          color="lightBlue"
          align="left"
          mb={2}
        >
          MY FAVORITE CORNER
        </Text>
        <Text>
          When I'm not at the computer, you can find me in my studio, behind
          my sewing machine, indulging in my obsession with design and
          creation.
          <br /> I love experimenting with fabrics, patterns, and textures,
          bringing my ideas to life through detailed craftsmanship and
          innovative designs. This hands-on approach to creativity not only
          fuels my passion but also inspires my work in the digital realm.
        </Text>

        <AboutScrollImage
          side="right"
          reduceMotion={reduceMotion}
          display={{ base: "block", sm: "none" }}
          width={{ base: "100%", sm: "50%" }}
          float="right"
          pl={{ base: "0", sm: "5" }}
          pt={{ base: "5", sm: "0" }}
          borderRadius="md"
        >
          <Image
            src="./nealphi_grayscale.jpg"
            width="100%"
            height="auto"
            display="block"
            alt="Studio and sewing"
          />
        </AboutScrollImage>
      </Box>
    </Flex>
  );
};

export default About;
